import React from 'react';
import { useRecoilValue } from 'recoil';
import { useParams } from 'react-router-dom';
import {
  Text,
  Box,
  Input,
  HStack,
  Button,
  VStack,
  Radio,
  RadioGroup,
  useToast,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import cuid from 'cuid';

import { ButtonVariants } from '~/customTheme/components/Button';
import CustomDrawer from '~/components/UI/Drawer';
import { selectedStoreIdAtom } from '~/recoil/atom';
import { Modifier, Option } from '~/types/store';
import { NewModifier } from './types';
import { addNewModifierInItem } from '~/firebase/store';

type NewModifierProps = {
  isOpen: boolean;
  onClose: () => void;
  modifiersInItem: string[];
  code: string;
};

const NewModifierDrawer: React.FC<NewModifierProps> = ({
  isOpen,
  onClose,
  modifiersInItem,
  code,
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    control,
  } = useForm<NewModifier>({
    defaultValues: { options: [{ name: '', price: '' }] },
    shouldUnregister: true,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
    shouldUnregister: true,
  });

  const { itemId = '' } = useParams();
  const toast = useToast();
  const storeId = useRecoilValue(selectedStoreIdAtom);

  const validateLimit = () => {
    const min = parseInt(getValues('min'));
    const max = parseInt(getValues('max'));

    return !isNaN(min) && !isNaN(max) && min <= max && min <= fields.length;
  };

  const validateNoSpace = (name: string) => {
    return name.trim().length !== 0;
  };

  const validateNoPoint = (name: string) => {
    return !name.includes('.');
  };

  const handleSaveNewModifier = async (data: NewModifier) => {
    const newModifierId = `${code}${cuid()}`;
    const newOptions: Option[] = data.options.map(({ name, price }) => {
      return {
        name,
        price: parseInt(price),
        id: `${code}${cuid()}`,
        isSoldOut: false,
        inventory: {
          defaultInventory: Infinity,
          currentInventory: Infinity,
        },
      };
    });
    const newModifier: Modifier = {
      code,
      name: data.name.trim(),
      max: parseInt(data.max),
      min: parseInt(data.min),
      required: data.required === 'true' ? true : false,
      options: newOptions,
    };
    const newModifiersInItem = [newModifierId, ...modifiersInItem];
    try {
      await addNewModifierInItem({
        storeId,
        itemId,
        newModifiersInItem,
        newModifierId,
        newModifier,
      });
      toast({
        position: 'top',
        title: '新增成功',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        position: 'top',
        title: '發生錯誤',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleNewOption = () => {
    append({ name: '', price: '' });
  };

  const handleDeleteOption = (index: number) => {
    remove(index);
  };

  const customButton = (
    <HStack bg="white" w="100%" justify="space-between" spacing={5} px="0.9rem" pb={4}>
      <Button isFullWidth variant={ButtonVariants.MODAL_PRIMARY} onClick={handleNewOption}>
        + 新增選項
      </Button>
    </HStack>
  );

  return (
    <CustomDrawer
      title="選項詳情設定"
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSubmit(handleSaveNewModifier)}
      customButton={customButton}
    >
      <Box as="label" justifyContent="space-between">
        <Text mb="0.2rem" htmlFor="name" color="#8F8F8F" fontSize="md">
          選項標題
        </Text>
        <Input
          {...register('name', { required: true, validate: (name) => validateNoSpace(name) })}
          isInvalid={!!errors.name}
          errorBorderColor="admin.error"
          focusBorderColor={!!errors.name ? 'admin.error' : 'admin.black.700'}
        />
      </Box>

      <Box as="label" justifyContent="space-between">
        <Text mb="0.2rem" htmlFor="required" color="#8F8F8F" fontSize="md">
          是否為必填選項？
        </Text>
        <HStack spacing="0.6rem">
          <RadioGroup
            {...register('required')}
            defaultValue="true"
            onChange={(e) => setValue('required', e)}
          >
            <HStack spacing={8} align="center">
              <Radio value="true" isInvalid={!!errors.required}>
                是
              </Radio>
              <Radio value="false" isInvalid={!!errors.required}>
                否
              </Radio>
            </HStack>
          </RadioGroup>
        </HStack>
      </Box>

      <Box as="label" justifyContent="space-between">
        <Text mb="0.2rem" htmlFor="min" color="#8F8F8F" fontSize="md">
          客戶可以選擇幾個選項？
        </Text>
        <HStack spacing={4} align="center">
          <Controller
            control={control}
            name="min"
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                w="3rem"
                {...field}
                isInvalid={!!error}
                errorBorderColor="admin.error"
                focusBorderColor={!!error ? 'admin.error' : 'admin.black.700'}
              >
                <NumberInputField textAlign="center" p={{ base: 0, sm: 4 }} />
              </NumberInput>
            )}
            rules={{ required: true, min: 0, validate: () => validateLimit() }}
          />
          <Text> - </Text>
          <Controller
            control={control}
            name="max"
            render={({ field, fieldState: { error } }) => (
              <NumberInput
                w="3rem"
                {...field}
                isInvalid={!!error}
                errorBorderColor="admin.error"
                focusBorderColor={!!error ? 'admin.error' : 'admin.black.700'}
              >
                <NumberInputField textAlign="center" p={{ base: 0, sm: 4 }} />
              </NumberInput>
            )}
            rules={{ required: true, min: 1, validate: () => validateLimit() }}
          />
        </HStack>
      </Box>

      <Box as="label" justifyContent="space-between">
        <HStack spacing={4} align="center">
          <Text color="#8F8F8F" fontSize="md" w="20rem">
            選項內容
          </Text>
          <Text color="#8F8F8F" fontSize="md" w="14rem">
            選項價錢
          </Text>
        </HStack>

        <VStack spacing={4} align="stretch" mt={1}>
          {fields.map((field, index) => (
            <Box key={field.id}>
              <HStack spacing={4} align="center">
                <Input
                  w="20rem"
                  h={10}
                  {...register(`options.${index}.name` as const, { required: true })}
                  isInvalid={!!(errors.options && errors.options[index]?.name)}
                  errorBorderColor="admin.error"
                  focusBorderColor={!!errors.options ? 'admin.error' : 'admin.black.700'}
                />
                <Controller
                  control={control}
                  name={`options.${index}.price` as const}
                  render={({ field, fieldState: { error } }) => (
                    <NumberInput
                      w="7rem"
                      {...field}
                      isInvalid={!!error}
                      errorBorderColor="admin.error"
                      focusBorderColor={!!error ? 'admin.error' : 'admin.black.700'}
                    >
                      <NumberInputField textAlign="center" p={{ base: 0, sm: 4 }} />
                    </NumberInput>
                  )}
                  rules={{ required: true, min: 0, validate: (price) => validateNoPoint(price) }}
                />
                <Button
                  w="5rem"
                  h={10}
                  bg="#FFEFEF"
                  color="admin.black"
                  borderRadius="5px"
                  fontWeight="normal"
                  _hover={{
                    color: 'white',
                    bg: 'admin.red.500',
                  }}
                  onClick={() => handleDeleteOption(index)}
                >
                  刪除
                </Button>
              </HStack>
            </Box>
          ))}
        </VStack>
      </Box>
    </CustomDrawer>
  );
};

export default NewModifierDrawer;
